import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import StripeConnectPanel from '../components/StripeConnectPanel';
import { startProductTour } from '../components/ProductTour';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Users, Shield, UserCheck, UserCog, RefreshCw, Mail, Bell, 
  Send, CheckCircle, AlertTriangle, MessageSquare, Clock,
  Plus, Trash2, Play, Calendar, History, Zap, Cloud
} from 'lucide-react';
import { toast } from 'sonner';
import { MFASetup } from '../components/MFAComponents';
import OnboardingModal from '../components/OnboardingModal';
import CurrencyPreferenceCard from '../components/CurrencyPreferenceCard';
import { useSelectedProviders } from '../hooks/useSelectedProviders';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { user, isAdmin } = useAuth();
  const { providerDetails, hasSelection, isDemo } = useSelectedProviders();
  const [loading, setLoading] = useState(true);
  const [cloudPickerOpen, setCloudPickerOpen] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [demoNotifications, setDemoNotifications] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: true,
    email_address: '',
    slack_enabled: false,
    notify_on_budget_warning: true,
    notify_on_budget_exceeded: true,
    notify_on_anomaly: true
  });
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [checkingBudgets, setCheckingBudgets] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [newReport, setNewReport] = useState({
    name: '',
    schedule_type: 'daily',
    time_hour: 8,
    day_of_week: 0,
    email_recipients: '',
    include_costs: true,
    include_anomalies: true,
    include_recommendations: true,
    enabled: true
  });

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchIntegrationStatus();
      fetchScheduledReports();
      fetchDemoNotifications();
    }
    fetchNotificationSettings();
    fetchMFAStatus();
  }, [isAdmin]);

  const fetchMFAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/auth/mfa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfaStatus(data);
    } catch (e) {
      console.error('Failed to fetch MFA status:', e);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/integrations/status`);
      setIntegrationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch integration status:', error);
    }
  };

  const fetchScheduledReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/scheduled-reports`);
      setScheduledReports(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduled reports:', error);
    }
  };

  const fetchDemoNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications/demo-log?limit=10`);
      setDemoNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch demo notifications:', error);
    }
  };

  const clearDemoNotifications = async () => {
    try {
      await axios.delete(`${API_URL}/notifications/demo-log`);
      setDemoNotifications([]);
      toast.success('Demo notification log cleared');
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications/settings`);
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/users/${userId}/role?role=${newRole}`);
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const saveNotificationSettings = async () => {
    try {
      await axios.put(`${API_URL}/notifications/settings`, notificationSettings);
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save notification settings');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setSendingTest(true);
    try {
      const response = await axios.post(`${API_URL}/notifications/test-email?recipient_email=${encodeURIComponent(testEmail)}`);
      if (response.data.mode === 'demo') {
        toast.success(`[DEMO] Test email simulated to ${testEmail}`);
      } else {
        toast.success(`Test email sent to ${testEmail}`);
      }
      fetchDemoNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const testSlackNotification = async () => {
    setTestingSlack(true);
    try {
      const response = await axios.post(`${API_URL}/notifications/test-slack`);
      if (response.data.mode === 'demo') {
        toast.success('[DEMO] Slack notification simulated');
      } else {
        toast.success('Test notification sent to Slack');
      }
      fetchDemoNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Slack notification');
    } finally {
      setTestingSlack(false);
    }
  };

  const checkBudgetsAndNotify = async () => {
    if (!notificationSettings.email_address) {
      toast.error('Please set a notification email address first');
      return;
    }
    setCheckingBudgets(true);
    try {
      const response = await axios.post(
        `${API_URL}/notifications/check-budgets?notify_email=${encodeURIComponent(notificationSettings.email_address)}`
      );
      const { checked, alerts_sent, mode } = response.data;
      if (alerts_sent > 0) {
        toast.success(`${mode === 'demo' ? '[DEMO] ' : ''}Checked ${checked} guardrails, sent ${alerts_sent} alert(s)`);
      } else {
        toast.success(`Checked ${checked} guardrails - all within budget`);
      }
      fetchDemoNotifications();
    } catch (error) {
      toast.error('Failed to check budgets');
    } finally {
      setCheckingBudgets(false);
    }
  };

  const createScheduledReport = async () => {
    try {
      const emails = newReport.email_recipients.split(',').map(e => e.trim()).filter(e => e);
      if (emails.length === 0) {
        toast.error('Please enter at least one email recipient');
        return;
      }
      
      await axios.post(`${API_URL}/scheduled-reports`, {
        ...newReport,
        email_recipients: emails
      });
      toast.success('Scheduled report created');
      setReportDialogOpen(false);
      setNewReport({
        name: '',
        schedule_type: 'daily',
        time_hour: 8,
        day_of_week: 0,
        email_recipients: '',
        include_costs: true,
        include_anomalies: true,
        include_recommendations: true,
        enabled: true
      });
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to create scheduled report');
    }
  };

  const deleteScheduledReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled report?')) return;
    try {
      await axios.delete(`${API_URL}/scheduled-reports/${reportId}`);
      toast.success('Scheduled report deleted');
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to delete scheduled report');
    }
  };

  const runReportNow = async (reportId) => {
    try {
      const response = await axios.post(`${API_URL}/scheduled-reports/${reportId}/run`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to run report');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400';
      case 'finance': return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-sky-900/30 dark:text-sky-400';
      default: return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'finance': return <UserCheck className="w-4 h-4" />;
      default: return <UserCog className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="settings-loading">
        <Skeleton className="h-8 w-48" />
        <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white">Settings</h1>
        <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
          Manage notifications, integrations, and scheduled reports
        </p>
      </div>

      {/* Profile Card */}
      <Card data-testid="profile-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Manrope']">Your Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{user?.name}</h3>
              <p className="text-slate-400">{user?.email}</p>
              <Badge className={getRoleColor(user?.role)}>
                {getRoleIcon(user?.role)}
                <span className="ml-1 capitalize">{user?.role}</span>
              </Badge>
            </div>
            <div className="ml-auto" data-tour="invite-team">
              <Button
                size="sm"
                variant="outline"
                onClick={startProductTour}
                className="border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                data-testid="replay-product-tour-btn"
              >
                Replay product tour
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Connect — one-click OAuth replaces restricted-key paste */}
      <StripeConnectPanel />

      {/* Two-Factor Authentication */}
      <Card data-testid="mfa-settings-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-500" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account with authenticator app and email verification
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mfaStatus?.mfa_enabled ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <CheckCircle className="w-6 h-6 text-emerald-500" />
                <div>
                  <p className="font-medium text-emerald-800 dark:text-emerald-300">MFA is enabled</p>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">
                    {mfaStatus.backup_codes_remaining} backup codes remaining
                    {mfaStatus.enabled_at && ` · Enabled ${new Date(mfaStatus.enabled_at).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={async () => {
                  const code = prompt('Enter your authenticator code to disable MFA:');
                  if (!code) return;
                  try {
                    const token = localStorage.getItem('token');
                    await axios.post(`${API_URL}/auth/mfa/disable`, { code, method: 'totp' }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success('MFA disabled');
                    fetchMFAStatus();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to disable MFA');
                  }
                }}
                data-testid="mfa-disable-btn"
              >
                Disable MFA
              </Button>
            </div>
          ) : (
            <MFASetup onComplete={fetchMFAStatus} />
          )}
        </CardContent>
      </Card>

      {/* Display Currency */}
      <CurrencyPreferenceCard />

      {/* Your Clouds — change selected providers */}
      <Card data-testid="your-clouds-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                <Cloud className="w-5 h-5 text-emerald-500" />
                Your Clouds
              </CardTitle>
              <CardDescription>
                CARTIEAI tailors every dashboard to the clouds you use. Change this anytime.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCloudPickerOpen(true)}
              data-testid="change-clouds-btn"
            >
              Change
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isDemo ? (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-cyan-900/20 border border-emerald-200 dark:border-cyan-800/50">
              <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-cyan-300 border-emerald-500/40 text-[11px]">DEMO</Badge>
              <p className="text-xs text-emerald-700 dark:text-cyan-300">
                You're signed in as the demo account — all 15 clouds are visible for showcasing.
              </p>
            </div>
          ) : !hasSelection ? (
            <div className="text-center py-4">
              <p className="text-sm text-slate-400 mb-3">No clouds selected yet.</p>
              <Button onClick={() => setCloudPickerOpen(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="choose-clouds-btn">
                Choose your clouds
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {providerDetails.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                  data-testid={`selected-cloud-${p.id}`}
                >
                  <div className="scale-50 -m-2"><p.LogoComponent /></div>
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{p.name}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Integration Status (Admin only) */}
      {isAdmin && integrationStatus && (
        <Card data-testid="integration-status-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Integration Status</CardTitle>
            <CardDescription>Status of notification integrations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg border ${integrationStatus.email.demo_mode ? 'border-amber-200 bg-amber-50/50 dark:bg-amber-900/10' : 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className={`w-5 h-5 ${integrationStatus.email.demo_mode ? 'text-amber-600' : 'text-emerald-600'}`} />
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white">Email ({integrationStatus.email.provider})</h4>
                      <p className="text-sm text-slate-400">
                        {integrationStatus.email.demo_mode ? 'Demo Mode - Notifications logged' : `Configured: ${integrationStatus.email.sender}`}
                      </p>
                    </div>
                  </div>
                  <Badge className={integrationStatus.email.demo_mode ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                    {integrationStatus.email.demo_mode ? 'Demo' : 'Active'}
                  </Badge>
                </div>
              </div>
              <div className={`p-4 rounded-lg border ${integrationStatus.slack.demo_mode ? 'border-amber-200 bg-amber-50/50 dark:bg-amber-900/10' : 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className={`w-5 h-5 ${integrationStatus.slack.demo_mode ? 'text-amber-600' : 'text-emerald-600'}`} />
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white">Slack</h4>
                      <p className="text-sm text-slate-400">
                        {integrationStatus.slack.demo_mode ? 'Demo Mode - Notifications logged' : 'Webhook configured'}
                      </p>
                    </div>
                  </div>
                  <Badge className={integrationStatus.slack.demo_mode ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                    {integrationStatus.slack.demo_mode ? 'Demo' : 'Active'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Demo Notification Log (Admin only) */}
      {isAdmin && demoNotifications.length > 0 && (
        <Card data-testid="demo-notifications-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-lg font-semibold font-['Manrope']">Demo Notification Log</CardTitle>
              </div>
              <Button variant="outline" size="sm" onClick={clearDemoNotifications} className="text-slate-400">
                <Trash2 className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
            <CardDescription>Simulated notifications (configure API keys for real delivery)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {demoNotifications.map((notif, idx) => (
                <div key={notif.id || idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  {notif.type === 'email' ? (
                    <Mail className="w-4 h-4 text-amber-500 mt-0.5" />
                  ) : (
                    <MessageSquare className="w-4 h-4 text-amber-500 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        {notif.type === 'email' ? 'Email' : 'Slack'}
                      </Badge>
                      <span className="text-xs text-slate-400">
                        {new Date(notif.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate mt-1">
                      {notif.subject || notif.message}
                    </p>
                    {notif.recipient && (
                      <p className="text-xs text-slate-400">To: {notif.recipient}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email & Slack Notifications (Admin only) */}
      {isAdmin && (
        <Card data-testid="notifications-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-emerald-600" />
              <CardTitle className="text-lg font-semibold font-['Manrope']">Notifications</CardTitle>
            </div>
            <CardDescription>Configure email and Slack alert notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Notification Email */}
            <div className="space-y-2">
              <Label htmlFor="notification-email">Default Notification Email</Label>
              <div className="flex gap-2">
                <Input
                  id="notification-email"
                  type="email"
                  placeholder="alerts@yourcompany.com"
                  value={notificationSettings.email_address || ''}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, email_address: e.target.value })}
                  className="flex-1"
                  data-testid="notification-email-input"
                />
                <Button onClick={saveNotificationSettings}>Save</Button>
              </div>
            </div>

            {/* Notification Toggles */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Budget Warning Alerts</Label>
                  <p className="text-sm text-slate-400">Notify when spending approaches threshold</p>
                </div>
                <Switch
                  checked={notificationSettings.notify_on_budget_warning}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, notify_on_budget_warning: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Budget Exceeded Alerts</Label>
                  <p className="text-sm text-slate-400">Notify when spending exceeds limit</p>
                </div>
                <Switch
                  checked={notificationSettings.notify_on_budget_exceeded}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, notify_on_budget_exceeded: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Anomaly Alerts</Label>
                  <p className="text-sm text-slate-400">Notify when cost anomalies are detected</p>
                </div>
                <Switch
                  checked={notificationSettings.notify_on_anomaly}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, notify_on_anomaly: checked })}
                />
              </div>
            </div>

            {/* Test Actions */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Test Email</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter email to test"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      data-testid="test-email-input"
                    />
                    <Button variant="outline" onClick={sendTestEmail} disabled={sendingTest} className="gap-2">
                      {sendingTest ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Test Slack</Label>
                  <Button 
                    variant="outline" 
                    onClick={testSlackNotification} 
                    disabled={testingSlack}
                    className="w-full gap-2"
                    data-testid="test-slack-btn"
                  >
                    {testingSlack ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                    Test Slack Notification
                  </Button>
                </div>
              </div>
              <Button 
                onClick={checkBudgetsAndNotify}
                disabled={checkingBudgets}
                className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="check-budgets-btn"
              >
                {checkingBudgets ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                Check All Budgets & Send Alerts
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduled Reports (Admin only) */}
      {isAdmin && (
        <Card data-testid="scheduled-reports-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  <CardTitle className="text-lg font-semibold font-['Manrope']">Scheduled Reports</CardTitle>
                </div>
                <CardDescription>Automated report delivery via email</CardDescription>
              </div>
              <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="add-report-btn">
                    <Plus className="w-4 h-4" />
                    Add Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create Scheduled Report</DialogTitle>
                    <DialogDescription>Set up automated report delivery</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Report Name</Label>
                      <Input
                        placeholder="Weekly Cost Summary"
                        value={newReport.name}
                        onChange={(e) => setNewReport({ ...newReport, name: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Schedule</Label>
                        <Select value={newReport.schedule_type} onValueChange={(v) => setNewReport({ ...newReport, schedule_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Time (Hour)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={newReport.time_hour}
                          onChange={(e) => setNewReport({ ...newReport, time_hour: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                    {newReport.schedule_type === 'weekly' && (
                      <div className="space-y-2">
                        <Label>Day of Week</Label>
                        <Select value={String(newReport.day_of_week)} onValueChange={(v) => setNewReport({ ...newReport, day_of_week: parseInt(v) })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Monday</SelectItem>
                            <SelectItem value="1">Tuesday</SelectItem>
                            <SelectItem value="2">Wednesday</SelectItem>
                            <SelectItem value="3">Thursday</SelectItem>
                            <SelectItem value="4">Friday</SelectItem>
                            <SelectItem value="5">Saturday</SelectItem>
                            <SelectItem value="6">Sunday</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Email Recipients (comma-separated)</Label>
                      <Input
                        placeholder="finance@company.com, cfo@company.com"
                        value={newReport.email_recipients}
                        onChange={(e) => setNewReport({ ...newReport, email_recipients: e.target.value })}
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>Include in Report</Label>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Cost Summary</span>
                        <Switch checked={newReport.include_costs} onCheckedChange={(c) => setNewReport({ ...newReport, include_costs: c })} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Anomalies</span>
                        <Switch checked={newReport.include_anomalies} onCheckedChange={(c) => setNewReport({ ...newReport, include_anomalies: c })} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Recommendations</span>
                        <Switch checked={newReport.include_recommendations} onCheckedChange={(c) => setNewReport({ ...newReport, include_recommendations: c })} />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createScheduledReport} className="bg-emerald-600 hover:bg-emerald-700">
                      Create Schedule
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {scheduledReports.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No scheduled reports</p>
                <p className="text-sm">Create a schedule to automate report delivery</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scheduledReports.map((report, idx) => (
                  <div key={report.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700" data-testid={`scheduled-report-${idx}`}>
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${report.enabled ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <Clock className={`w-5 h-5 ${report.enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">{report.name}</h4>
                        <p className="text-sm text-slate-400">
                          {report.schedule_type.charAt(0).toUpperCase() + report.schedule_type.slice(1)} at {report.time_hour}:00
                          {report.schedule_type === 'weekly' && ` (${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][report.day_of_week || 0]})`}
                        </p>
                        <p className="text-xs text-slate-400">{report.email_recipients.join(', ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={report.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}>
                        {report.enabled ? 'Active' : 'Paused'}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => runReportNow(report.id)} title="Run Now">
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteScheduledReport(report.id)} className="text-rose-600 hover:text-rose-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* User Management (Admin only) */}
      {isAdmin && (
        <Card data-testid="user-management-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold font-['Manrope']">User Management</CardTitle>
                <CardDescription>Manage user roles and permissions</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchUsers}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {users.map((u, idx) => (
                <div key={u.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700" data-testid={`user-item-${idx}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <span className="font-medium text-slate-400 dark:text-slate-400">{u.name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{u.name}</p>
                      <p className="text-sm text-slate-400">{u.email}</p>
                    </div>
                  </div>
                  <Select value={u.role} onValueChange={(value) => updateUserRole(u.id, value)} disabled={u.id === user?.id}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="engineering">Engineering</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role Permissions */}
      <Card data-testid="permissions-info-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Manrope']">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50/50 dark:bg-rose-900/10">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-rose-600" />
                <h3 className="font-semibold text-rose-700 dark:text-rose-400">Admin</h3>
              </div>
              <ul className="text-sm text-slate-400 dark:text-slate-400 space-y-1">
                <li>• Full platform access</li>
                <li>• Manage integrations</li>
                <li>• Schedule reports</li>
                <li>• User management</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg border border-emerald-200 dark:border-sky-800 bg-emerald-50/50 dark:bg-sky-900/10">
              <div className="flex items-center gap-2 mb-2">
                <UserCheck className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-emerald-700 dark:text-sky-400">Finance</h3>
              </div>
              <ul className="text-sm text-slate-400 dark:text-slate-400 space-y-1">
                <li>• View dashboards</li>
                <li>• Access analytics</li>
                <li>• Export reports</li>
                <li>• Monitor budgets</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10">
              <div className="flex items-center gap-2 mb-2">
                <UserCog className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-emerald-700 dark:text-emerald-400">Engineering</h3>
              </div>
              <ul className="text-sm text-slate-400 dark:text-slate-400 space-y-1">
                <li>• View dashboards</li>
                <li>• Optimization insights</li>
                <li>• Resolve anomalies</li>
                <li>• Add cost data</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cloud picker modal — reuses OnboardingModal in "edit" mode */}
      <OnboardingModal
        isOpen={cloudPickerOpen}
        allowClose
        onComplete={() => setCloudPickerOpen(false)}
        userEmail={user?.email}
      />
    </div>
  );
};

export default Settings;
